import { TierBadge } from "@/components/StatusBadge";
import { Info } from "lucide-react";

const TIERS = [
  { name: "Unrated", range: "0 completed", desc: "Entry-level / trial assignments only" },
  { name: "Bronze", range: "60–74", desc: "Standard assignments" },
  { name: "Silver", range: "75–84", desc: "Standard + mid-priority" },
  { name: "Gold", range: "85–92", desc: "High-priority + specialized (AI / Payments / A11y / Perf)" },
  { name: "Platinum", range: "93–100", desc: "Top-tier assignments · highest payouts" },
];

const TIER_ACCENT = {
  Unrated: "border-slate-200 bg-slate-50 text-slate-600",
  "Below Threshold": "border-red-200 bg-red-50 text-red-700",
  Bronze: "border-amber-200 bg-amber-50 text-amber-800",
  Silver: "border-slate-300 bg-slate-100 text-slate-700",
  Gold: "border-amber-300 bg-amber-100 text-amber-800",
  Platinum: "border-cyan-700 bg-cyan-900 text-cyan-100",
};

/**
 * Tester Tier card — shows the current tier badge, score, range,
 * and highlights where the tester sits on the full ladder.
 *
 * Props:
 *  - tier: string (Unrated | Below Threshold | Bronze | Silver | Gold | Platinum)
 *  - score: number | null
 *  - state: "unrated" | "provisional" | "established"
 *  - completedCount: number
 *  - compact: boolean — when true, hides the ladder legend (for admin side-view)
 */
export default function TesterTierCard({
  tier = "Unrated",
  score,
  state = "unrated",
  completedCount = 0,
  compact = false,
}) {
  const currentTier = tier || "Unrated";
  const info = TIERS.find(t => t.name === currentTier) || TIERS[0];
  const isBelow = currentTier === "Below Threshold";
  const displayScore = score == null ? "—" : Math.round(score);

  return (
    <div className="kpi-card" data-testid="tester-tier-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase text-slate-500 tracking-wider">Tester Tier</div>
          <div className="mt-1"><TierBadge tier={currentTier} state={state} /></div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-bold text-slate-900 leading-none" data-testid="tier-card-score">{displayScore}</div>
          <div className="text-[10px] uppercase text-slate-500 tracking-wider mt-0.5">/ 100 Score</div>
        </div>
      </div>

      <div className={`p-3 rounded-md border ${TIER_ACCENT[currentTier] || TIER_ACCENT.Unrated}`} data-testid="tier-card-current">
        <div className="text-[11px] uppercase tracking-wider opacity-70">
          {isBelow ? "Below tier threshold" : `${currentTier} range`}
        </div>
        <div className="font-display font-bold">
          {isBelow ? "Score < 60" : info.range}
        </div>
        <div className="text-[11px] mt-1 opacity-80 leading-snug">
          {isBelow
            ? "Boost bug quality, on-time delivery, and reliability to move into Bronze (60+)."
            : info.desc}
        </div>
        <div className="text-[11px] mt-2 opacity-80">
          Based on {completedCount} completed assignment{completedCount === 1 ? "" : "s"}
          {state === "provisional" && " · Provisional (5+ needed for Gold/Platinum)"}
        </div>
      </div>

      {!compact && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
            <Info className="w-3 h-3"/> Full ladder
          </div>
          <div className="space-y-1" data-testid="tier-card-ladder">
            {TIERS.map(t => (
              <div
                key={t.name}
                className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
                  t.name === currentTier
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`ladder-row-${t.name}`}
              >
                <span className="font-medium">{t.name}</span>
                <span className={`text-[11px] ${t.name === currentTier ? "text-cyan-300" : "text-slate-400"}`}>{t.range}</span>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 mt-2 leading-snug">
            Gold and Platinum require at least 5 completed assignments even if the raw score qualifies earlier.
          </div>
        </div>
      )}
    </div>
  );
}
